import { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowLeft,
  LayoutDashboard,
  Activity,
  FileDown,
  Wrench,
  AlertCircle,
  Check,
  Loader2,
  Lock,
  Search,
  ShieldCheck,
  Smartphone,
  MousePointer2,
  Zap,
  Monitor,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CategoryScore, FixStep, DiagnosticReport, CheckResult, FixGuidance } from '../services/diagnosticEngine';
import { useHistory } from '../context/HistoryContext';
import { useAuth } from '../context/AuthContext';
import styles from './ReportPage.module.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { HistoryService } from '../services/historyService';
import { useDiagnosticContext } from '../context/DiagnosticContext';
import {
  deductReportCredit,
  stashDevCreditNotice,
  consumeDevCreditNotice,
} from '../services/useCredit';

type EnrichedCheck = CheckResult & { guidance?: FixGuidance };

interface FullReport extends DiagnosticReport {
  tier?: 'free' | 'pro';
  lockedChecksCount?: number;
  vendorUpdateGuidance?: FixGuidance;
  proExtras?: any | null;
  overview?: {
    os: string;
    manufacturer: string;
    model: string;
    systemType: string;
    ramGB: number;
    diskTotalGB: number;
    diskFreeGB: number;
    uptimeDays: number;
    startupCount: number;
    serialNumber: string;
    processor: string;
  };
  battery?: {
    present?: boolean;
    designCapacity: number;
    fullChargeCapacity: number;
    wearPercent: number;
    cycleCount: number;
    status: string;
    acStatus: string;
  } | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const valOrNA = (val: any) => {
  if (val === null || val === undefined || val === '' || val === 0 || val === '0' || val === 'Unknown' || val === 'Not Available') {
    return <span style={{ opacity: 0.4, fontStyle: 'italic', fontWeight: 400 }}>Not Detected</span>;
  }
  return val;
};

const getHealthRating = (score: number) => {
  if (score >= 90) return { label: 'Excellent', class: styles.excellent };
  if (score >= 75) return { label: 'Good', class: styles.good };
  if (score >= 50) return { label: 'Fair', class: styles.fair };
  if (score >= 25) return { label: 'Needs Attention', class: styles.needs_attention };
  return { label: 'Critical', class: styles.critical };
};

function OverallHealthSummary({ score }: { score: number }) {
  const rating = getHealthRating(score);
  return (
    <div className={styles.overallHealthCard}>
      <div className={styles.healthRating}>
        <h4>Overall PC Health</h4>
        <div className={`${styles.ratingValue} ${rating.class}`}>{rating.label}</div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-3)', margin: 0 }}>
          Based on 80 standard diagnostic points.
        </p>
      </div>
      <div className={styles.healthScoreSmall}>
        <strong>{score}</strong>
        <span>Health Points</span>
      </div>
    </div>
  );
}

// ── StartupRow: row with expandable technical details ─────────────────────────
function StartupRow({
  name, startupType, recText, recColor, rawLocation, rawCommand,
}: {
  name: string; startupType: string; recText: string; recColor: string;
  rawLocation?: string; rawCommand?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasTech = !!(rawLocation || rawCommand);
  return (
    <>
      <tr style={{ transition: 'background 0.2s' }}>
        <td style={{ fontWeight: 500, padding: '12px 10px' }}>{name || 'Unknown App'}</td>
        <td style={{ color: '#b0b0c0', fontSize: '0.85rem', padding: '12px 10px' }}>{startupType}</td>
        <td style={{ color: recColor, fontSize: '0.82rem', padding: '12px 10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>{recText}</span>
            {hasTech && (
              <button
                onClick={() => setExpanded(v => !v)}
                style={{
                  alignSelf: 'flex-start', fontSize: '0.73rem',
                  background: 'none', border: 'none', color: '#5577aa',
                  cursor: 'pointer', padding: 0, textDecoration: 'underline',
                  opacity: 0.8,
                }}
              >
                {expanded ? 'Hide technical details ▲' : 'Technical details ▼'}
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && hasTech && (
        <tr>
          <td colSpan={3} style={{
            background: 'rgba(0,0,0,0.3)', fontSize: '0.75rem', fontFamily: 'monospace',
            color: '#7788aa', padding: '16px 20px', wordBreak: 'break-all',
            borderLeft: '2px solid var(--accent)',
            boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.2)',
          }}>
            {rawLocation && (
              <div style={{ marginBottom: 6 }}>
                <strong style={{ color: 'var(--accent)', marginRight: 6 }}>REGISTRY:</strong> {rawLocation}
              </div>
            )}
            {rawCommand  && (
              <div>
                <strong style={{ color: 'var(--accent)', marginRight: 6 }}>COMMAND:</strong> {rawCommand}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Startup Programs helpers ──────────────────────────────
const friendlyLocation = (loc: string): string => {
  if (!loc) return 'Startup entry';
  const l = loc.toUpperCase();
  // Registry checks
  if (l.includes('HKEY_CURRENT_USER') || l.startsWith('HKU\\') || l.startsWith('HKCU\\'))
    return 'Starts when this user signs in';
  if (l.includes('HKEY_LOCAL_MACHINE') || l.startsWith('HKLM\\'))
    return 'Starts for all users';
  // Folder checks
  if (l.includes('\\MICROSOFT\\WINDOWS\\START MENU\\PROGRAMS\\STARTUP'))
    return 'Startup folder shortcut';
  if (l.includes('STARTUP') && !l.startsWith('HK'))
    return 'Startup folder shortcut';
  // Task checks
  if (l.includes('TASK') || l.includes('SCHEDULE') || l.includes('\\TASKS\\'))
    return 'Scheduled task at login';
  
  return 'Startup entry';
};

const startupRecommendation = (name: string): { text: string; keep: boolean | null } => {
  if (!name) return { text: 'Review — disable if you don\'t need it open at login', keep: null };
  const n = name.toLowerCase();
  
  // Always keep / Critical components
  if (/defender|security|malware|antivirus|realtek|nvidia|amd|intel|audio|sound|touchpad|synaptics|elan|bluetooth|wifi|vpn|windows|microsoft/i.test(n))
    return { text: 'Keep enabled — system component', keep: true };
  
  // Cloud sync
  if (/googledrivefs|google drive|onedrive/i.test(n))
    return { text: 'Keep if you use cloud sync, disable if not needed at startup', keep: null };
  
  // Safe to disable
  if (/teams|spotify|steam|discord|adobe|zoom|slack|skype|dropbox|epic games|origin|battle\.?net|ubisoft|razer|logitech gh?ub|lghub/i.test(n))
    return { text: 'Safe to disable from startup', keep: false };
    
  return { text: 'Review — disable if you don\'t need it open at login', keep: null };
};

// ── Battery Health Intelligence ─────────────────────────────
function BatteryIntelligenceCard({ battery }: { battery: FullReport['battery'] }) {
  const isDev = window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1');

  const hasCapacity = !!(battery && (battery.designCapacity > 0 || battery.fullChargeCapacity > 0));
  const hasPowerTelemetry = !!(
    battery?.acStatus &&
    battery.acStatus !== 'Unknown' &&
    battery.acStatus !== 'Not Detected'
  );
  if (!battery || (!hasCapacity && !hasPowerTelemetry)) {
    const isActuallyLaptop = battery?.present;
    return (
      <div className={styles.desktopFallback}>
        {isActuallyLaptop ? <Smartphone size={32} style={{ marginBottom: '16px', opacity: 0.5 }} /> : <Monitor size={32} style={{ marginBottom: '16px', opacity: 0.5 }} />}
        <h3>{isActuallyLaptop ? 'Battery Data Not Available' : 'Desktop System Detected'}</h3>
        <p>
          {isActuallyLaptop 
            ? 'A portable system was detected, but detailed battery health data could not be retrieved from this scan.' 
            : 'Battery diagnostics and wear analysis are not applicable to desktop PCs.'}
        </p>
        {isDev && (
          <div style={{ fontSize: '10px', opacity: 0.4, marginTop: '10px', fontFamily: 'monospace' }}>
            DEBUG: present={String(battery?.present)} | des={battery?.designCapacity} | full={battery?.fullChargeCapacity} | status={battery?.status}
          </div>
        )}
      </div>
    );
  }

  const wear = battery.wearPercent;
  let rating = 'Excellent';
  let ratingClass = styles.excellent;
  let recommendation = {
    title: 'Battery Healthy',
    text: 'Your battery is in peak condition. To maintain this, avoid leaving it at 100% for long periods.',
    icon: <CheckCircle2 className={styles.recIcon} size={24} />
  };

  if (wear > 50) {
    rating = 'Replace Soon';
    ratingClass = styles.replace;
    recommendation = {
      title: 'Replacement Recommended',
      text: 'Your battery has lost over half its original capacity. Runtime will be very short and the laptop may shut down unexpectedly.',
      icon: <AlertTriangle className={styles.recIcon} size={24} />
    };
  } else if (wear > 35) {
    rating = 'Poor';
    ratingClass = styles.poor;
    recommendation = {
      title: 'Significant Degradation',
      text: 'Your battery is aged. Expect significantly reduced mobile runtime. Consider a replacement if portability is important.',
      icon: <AlertCircle className={styles.recIcon} size={24} />
    };
  } else if (wear > 20) {
    rating = 'Fair';
    ratingClass = styles.fair;
    recommendation = {
      title: 'Moderate Wear',
      text: 'The battery shows normal signs of use. You can extend its life by keeping it between 20% and 80% charge.',
      icon: <Info className={styles.recIcon} size={24} />
    };
  } else if (wear > 10) {
    rating = 'Good';
    ratingClass = styles.good;
    recommendation = {
      title: 'Good Condition',
      text: 'Your battery is performing well. Minor capacity loss is expected with age.',
      icon: <CheckCircle2 className={styles.recIcon} size={24} />
    };
  }

  const cycleStatus = battery.cycleCount < 0 ? 'Unknown' : battery.cycleCount > 500 ? 'Aged' : battery.cycleCount > 250 ? 'Moderate' : 'New';

  return (
    <div className={styles.batteryCard}>
      <div className={styles.batteryHeader}>
        <div className={styles.batteryTitle}>
          <Zap size={24} className={styles.recIcon} />
          <h3>Battery Health Intelligence</h3>
        </div>
        <div className={`${styles.healthBadge} ${ratingClass}`}>{rating}</div>
      </div>

      <div className={styles.batteryMainGrid}>
        <div className={styles.wearVisual}>
          <div className={styles.wearRing}>
            <svg viewBox="0 0 100 100" className={styles.scoreRing}>
              <circle cx="50" cy="50" r="45" className={styles.ringBg} />
              <circle 
                cx="50" cy="50" r="45" 
                className={styles.ringFill} 
                style={{ 
                  stroke: wear > 35 ? '#ff4444' : wear > 20 ? '#ff9800' : '#00e676',
                  strokeDashoffset: 283 - (283 * (100 - wear)) / 100 
                }} 
              />
            </svg>
            <div className={styles.wearValue}>
              <strong>{100 - wear}%</strong>
              <span>Health</span>
            </div>
          </div>
          <p style={{ marginTop: 16, fontSize: '0.8rem', color: '#888' }}>
            {wear}% capacity lost since new
          </p>
        </div>

        <div className={styles.batteryMetrics}>
          <div className={styles.metricItem}>
            <label><Info size={12} /> Design Capacity</label>
            <strong>{battery.designCapacity > 0 ? `${battery.designCapacity.toLocaleString()} mWh` : valOrNA(null)}</strong>
          </div>
          <div className={styles.metricItem}>
            <label><Info size={12} /> Full Charge</label>
            <strong>{battery.fullChargeCapacity > 0 ? `${battery.fullChargeCapacity.toLocaleString()} mWh` : valOrNA(null)}</strong>
          </div>
          <div className={styles.metricItem}>
            <label><Activity size={12} /> Cycles</label>
            <strong>{battery.cycleCount < 0 ? 'Not Reported by Device' : battery.cycleCount} <span style={{ fontSize: '0.7rem', color: '#888' }}>({cycleStatus})</span></strong>
          </div>
          <div className={styles.metricItem}>
            <label><Zap size={12} /> Status</label>
            <strong>{battery.status === 'Not Detected' && battery.present ? 'Present' : valOrNA(battery.status)}</strong>
          </div>
          <div className={styles.metricItem}>
            <label><Zap size={12} /> Power Source</label>
            <strong>
              {(() => {
                const ps = battery.acStatus;
                if (!ps || ps === 'Unknown' || ps === 'Not Detected') {
                  return valOrNA(ps);
                }
                if (ps === 'Plugged In' || ps === 'On Battery') {
                  return ps === 'Plugged In' ? 'AC Adapter Connected' : 'Running on Battery';
                }
                return ps;
              })()}
            </strong>
          </div>
          <div className={styles.metricItem}>
            <label><Activity size={12} /> Lifespan</label>
            <strong>{wear > 50 ? 'Replace Soon' : wear > 35 ? '< 1 Year' : wear > 20 ? '1-2 Years' : '3+ Years'}</strong>
          </div>
        </div>
      </div>

      <div className={styles.batteryRecommendations}>
        <div className={styles.recommendationBox}>
          {recommendation.icon}
          <div className={styles.recText}>
            <h4>{recommendation.title}</h4>
            <p>{recommendation.text}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────

export function ReportPage() {
  const navigate = useNavigate();
  const { profile, user, refreshProfile } = useAuth();
  const { latestReport, refreshHistory } = useHistory();
  const { resetDiagnostic } = useDiagnosticContext();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCreditNotice, setDevCreditNotice] = useState<string | null>(null);
  const [expandedCheckId, setExpandedCheckId] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const notice = consumeDevCreditNotice();
    if (notice) setDevCreditNotice(notice);
  }, []);

  if (!latestReport) {
    return (
      <div className="app">
        <Header />
        <main className={styles.reportPage}>
          <div className="wrap">
            <div className={styles.noReport}>
              <Search size={48} />
              <h2>No Report Found</h2>
              <p>You haven't generated a diagnostic report yet.</p>
              <button onClick={() => { resetDiagnostic(); navigate('/diagnostic'); }}>Start Diagnostic</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const report = latestReport.reportData as unknown as FullReport;
  const isPro = latestReport.isPro;
  const overview = report.overview || {
    os: 'Windows',
    manufacturer: report.manufacturer || 'Unknown',
    model: report.model || 'Unknown',
    systemType: 'x64-based PC',
    ramGB: 0,
    diskTotalGB: 0,
    diskFreeGB: 0,
    uptimeDays: 0,
    startupCount: 0,
    serialNumber: '',
    processor: '',
  };

  // -------------------------------------------------------------------------
  // PDF download — Pro only.
  // -------------------------------------------------------------------------
  const downloadPDF = async () => {
    if (!reportRef.current || !isPro) return;
    setIsGenerating(true);
    setError(null);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0a0a0b',
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const timestamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
      pdf.save(`PCCheckr-Pro-Report-${latestReport.id}-${timestamp}.pdf`);
    } catch (err) {
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpgrade = async () => {
    if (!user || !profile || isUpgrading) return;
    if (profile.credits <= 0) {
      navigate('/pricing');
      return;
    }
    setIsUpgrading(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const creditResult = await deductReportCredit(idToken, { currentCredits: profile.credits });

      if (creditResult.devMode && creditResult.devMessage) {
        stashDevCreditNotice(creditResult.devMessage);
        setDevCreditNotice(creditResult.devMessage);
      } else {
        await refreshProfile();
      }

      // For the in-place upgrade we redirect back into the diagnostic flow so
      // the Pro report is regenerated from the original scan with all 80 checks.
      // We don't promote the trimmed free report — that would still be missing
      // the Pro check data.
      navigate('/diagnostic');

      // Mark the existing free report so it isn't accidentally shown again.
      const history = HistoryService.getHistory();
      const reportIndex = history.findIndex(r => r.id === latestReport.id);
      if (reportIndex !== -1) {
        history.splice(reportIndex, 1);
        localStorage.setItem('pccheckr_history', JSON.stringify(history));
      }
      refreshHistory();
    } catch (err: any) {
      console.error('Upgrade failed:', err);
      setError(err.message || 'Failed to process credit. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  // =========================================================================
  // FREE PREVIEW LAYOUT
  // =========================================================================
  if (!isPro) {
    return (
      <div className="app">
        <Header />
        <main className={styles.reportPage}>
          <div className="wrap">
            <ErrorBoundary>
              <div ref={reportRef} id="report-content">
                <header className={styles.reportHeader}>
              <div className={styles.meta} data-html2canvas-ignore="true">
                <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>
                  <ArrowLeft size={16} /> Dashboard
                </button>
                <span className={styles.date}>Generated: {new Date(latestReport.timestamp).toLocaleDateString()}</span>
              </div>
              <div className={styles.titleRow}>
                <h1>Free Diagnostic Preview</h1>
                <div className={styles.betaBadge}>FREE PREVIEW</div>
              </div>
              <div className={styles.reportId}>Report ID: {latestReport.id}</div>
              <div className={styles.disclaimer}>
                <ShieldCheck size={16} />
                <span>
                  Basic preview mode — 6 of 80 diagnostic checks shown. No personal documents,
                  passwords, photos, or browsing history are collected.
                </span>
              </div>
            </header>

            <div className={styles.reportGrid}>
              <section className={styles.scoreSection}>
                <div className={styles.scoreCard}>
                  <div className={styles.scoreVisual}>
                    <svg className={styles.scoreRing} viewBox="0 0 100 100">
                      <circle className={styles.ringBg} cx="50" cy="50" r="45" />
                      <circle
                        className={styles.ringFill}
                        cx="50"
                        cy="50"
                        r="45"
                        style={{
                          strokeDashoffset: 283 - (283 * report.healthScore) / 100,
                          stroke: report.healthScore < 40 ? '#ff4444' : report.healthScore < 75 ? '#ff9800' : '#00e676',
                        }}
                      />
                    </svg>
                    <div className={styles.scoreText}>
                      <span className={styles.number}>{report.healthScore}</span>
                      <span className={styles.label}>Basic Score</span>
                    </div>
                  </div>
                  <div className={styles.scoreStatus}>
                    <h3 style={{ textTransform: 'capitalize' }}>Basic Health Indicator</h3>
                    <div className={styles.countsGrid}>
                      <div className={`${styles.countBadge} ${styles.pass}`}>
                        <strong>{report.passCount}</strong><span>Passed</span>
                      </div>
                      <div className={`${styles.countBadge} ${styles.warning}`}>
                        <strong>{report.warningCount}</strong><span>Warning</span>
                      </div>
                      <div className={`${styles.countBadge} ${styles.critical}`}>
                        <strong>{report.criticalCount}</strong><span>Critical</span>
                      </div>
                      <div className={`${styles.countBadge} ${styles.not_detected}`}>
                        <strong>{report.notDetectedCount}</strong><span>Not Found</span>
                      </div>
                    </div>
                    <p>6 core diagnostic points analysed.</p>
                    <div className={styles.lockedCountBadge}>
                      <Lock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      74 advanced checks locked
                    </div>
                  </div>
                </div>

                {/* Real system snapshot — same values as upload preview */}
                <div className={styles.overviewSection} style={{ gridTemplateColumns: '1fr' }}>
                  <div className={styles.overCard}>
                    <div className={styles.overHeader}>
                      <Smartphone size={18} />
                      <h3>System Snapshot</h3>
                    </div>
                    <div className={styles.overGrid}>
                      <div className={styles.overItem}>
                        <span>Operating System</span><strong>{valOrNA(overview.os)}</strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>Manufacturer</span><strong>{valOrNA(overview.manufacturer)}</strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>Model</span><strong>{valOrNA(overview.model)}</strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>System Type</span><strong>{valOrNA(overview.systemType)}</strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>Installed RAM</span><strong>{overview.ramGB > 0 ? `${overview.ramGB} GB` : valOrNA(0)}</strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>C: Drive</span>
                        <strong>
                          {overview.diskTotalGB > 0 ? `${overview.diskFreeGB} GB free of ${overview.diskTotalGB} GB` : valOrNA(0)}
                        </strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>System Uptime</span>
                        <strong className={overview.uptimeDays > 7 ? styles.warn : styles.ok}>
                          {overview.uptimeDays} days
                        </strong>
                      </div>
                      <div className={styles.overItem}>
                        <span>Startup Programs</span><strong>{valOrNA(overview.startupCount)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.contentSection}>
                <div className={styles.sectionHeader}>
                  <Search size={20} className={styles.searchIcon} />
                  <h2>Free Diagnostic Findings (6)</h2>
                </div>

                <div className={styles.freeBasicFindings}>
                  <div className={styles.issueList}>
                    {report.allChecks.map((check: CheckResult) => (
                      <div
                        key={check.id}
                        className={`${styles.issueCard} ${styles.mini} ${
                          styles[check.status === 'fail' ? 'high' : check.status === 'warning' ? 'medium' : 'healthy']
                        }`}
                      >
                        <div className={styles.issueTop}>
                          <div className={styles.titleWrap}>
                            <h5>{check.name}</h5>
                          </div>
                          <span
                            className={styles.severityTag}
                            style={{
                              background:
                                check.status === 'fail' ? 'rgba(255,68,68,0.12)'
                                : check.status === 'warning' ? 'rgba(255,152,0,0.12)'
                                : 'rgba(0,230,118,0.12)',
                              color:
                                check.status === 'fail' ? '#ff4444'
                                : check.status === 'warning' ? '#ff9800'
                                : '#00e676',
                            }}
                          >
                            {check.status === 'fail' ? 'Critical' : check.status === 'warning' ? 'Warning' : check.status === 'pass' ? 'OK' : 'N/A'}
                          </span>
                        </div>
                        <p className={styles.miniEvidence}>{check.evidence}</p>
                        {check.recommendation && (
                          <p className={styles.miniEvidence} style={{ marginTop: 6, color: '#9aa' }}>
                            <strong>Tip:</strong> {check.recommendation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Basic recommendation summary */}
                <div className={styles.bottleneckCard} style={{ borderColor: 'rgba(0,200,230,0.25)', background: 'rgba(0,200,230,0.05)' }}>
                  <h3 style={{ color: '#00c8e6' }}><Info size={18} /> Basic Recommendations</h3>
                  <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--text-1)' }}>
                    {overview.uptimeDays > 3 && <li>Restart your PC — it has been running for {overview.uptimeDays} days.</li>}
                    {overview.diskFreeGB > 0 && overview.diskFreeGB < 20 && <li>Free up space on C: — only {overview.diskFreeGB} GB remaining.</li>}
                    {overview.startupCount > 12 && <li>Disable some of your {overview.startupCount} startup programs.</li>}
                    {overview.ramGB > 0 && overview.ramGB < 8 && <li>Consider a RAM upgrade — {overview.ramGB} GB is below modern minimum.</li>}
                    <li>Run Windows Update regularly to install the latest stability and security patches.</li>
                  </ul>
                </div>

                {/* Locked Pro teaser */}
                <div className={styles.freePreviewLocked}>
                  <div className={styles.lockVisual}><Lock size={48} /></div>
                  <h2>74 Advanced Checks Locked</h2>
                  <p>Upgrade to Pro to unlock the full <strong>80-point forensic breakdown</strong> of your system.</p>

                  <div className={styles.lockedFeaturesTeaser}>
                    <h4>Locked in Free Preview:</h4>
                    <div className={styles.teaserGrid}>
                      <div className={styles.teaserItem}><Lock size={12} /> Storage Health &amp; S.M.A.R.T</div>
                      <div className={styles.teaserItem}><Lock size={12} /> BSOD &amp; Kernel Crash Analysis</div>
                      <div className={styles.teaserItem}><Lock size={12} /> Driver Stability &amp; Latency</div>
                      <div className={styles.teaserItem}><Lock size={12} /> WiFi &amp; Network Performance</div>
                      <div className={styles.teaserItem}><Lock size={12} /> Security &amp; UAC Vulnerabilities</div>
                      <div className={styles.teaserItem}><Lock size={12} /> Windows Update Health</div>
                      <div className={styles.teaserItem}><Lock size={12} /> Primary Bottleneck Detection</div>
                      <div className={styles.teaserItem}><Lock size={12} /> Prioritized Fix Plan</div>
                    </div>
                  </div>

                  <div className={styles.previewUpgradeCta}>
                    <h3>Unlock the full Pro report:</h3>
                    <ul>
                      <li><Check size={14} /> Full 80-point engineer-grade breakdown</li>
                      <li><Check size={14} /> Prioritized fix plan with step-by-step actions</li>
                      <li><Check size={14} /> Root-cause identification for crashes &amp; slowness</li>
                      <li><Check size={14} /> Downloadable Pro PDF for your records</li>
                    </ul>
                    {(profile?.credits || 0) > 0 ? (
                      <button className={styles.fullUpgradeBtn} onClick={handleUpgrade} disabled={isUpgrading}>
                        {isUpgrading ? 'Processing…' : 'Unlock Full 80-Point Report — Use 1 Credit'}
                      </button>
                    ) : (
                      <button className={styles.fullUpgradeBtn} onClick={() => navigate('/pricing')}>
                        Unlock Full Report — Buy Credits
                      </button>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div className="wrap">
            {devCreditNotice && (
              <div className={styles.pdfErrorMsg} style={{ background: 'rgba(0,200,230,0.08)', borderColor: 'rgba(0,200,230,0.25)', color: '#8ec8d8' }}>
                <Info size={16} /><span>{devCreditNotice}</span>
              </div>
            )}
            {error && (
              <div className={styles.pdfErrorMsg}>
                <AlertTriangle size={16} /><span>{error}</span>
              </div>
            )}

            <footer className={styles.reportActions}>
              {(profile?.credits || 0) > 0 ? (
                <button className={styles.primaryAction} onClick={handleUpgrade} disabled={isUpgrading}>
                  {isUpgrading ? <><Loader2 size={18} className={styles.spin} /> Processing…</> : <><Zap size={18} /> Generate Full Pro Report — Use 1 Credit</>}
                </button>
              ) : (
                <button className={styles.primaryAction} onClick={() => navigate('/pricing')}>
                  <Zap size={18} /> Unlock Full Report — Buy Credits
                </button>
              )}
              <button className={styles.secondaryAction} onClick={() => navigate('/dashboard')}>
                <LayoutDashboard size={18} /> Back to Dashboard
              </button>
            </footer>
          </div>
        </ErrorBoundary>
      </div>
        </main>
        <Footer />
      </div>
    );
  }

  // =========================================================================
  // PRO REPORT LAYOUT — full 80-check forensic view
  // =========================================================================
  return (
    <div className="app">
      <Header />
      <main className={styles.reportPage}>
        <div className="wrap">
          <ErrorBoundary>
            <div ref={reportRef} id="report-content">
              <header className={styles.reportHeader}>
                <div className={styles.meta} data-html2canvas-ignore="true">
              <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={16} /> Dashboard
              </button>
              <span className={styles.date}>Generated: {new Date(latestReport.timestamp).toLocaleDateString()}</span>
            </div>
            <div className={styles.titleRow}>
              <h1>Pro Diagnostic Report</h1>
              <div className={styles.betaBadge}>PRO VERSION</div>
            </div>
            <div className={styles.reportId}>Report ID: {latestReport.id}</div>
            <div className={styles.disclaimer}>
              <ShieldCheck size={16} />
              <span>
                Full 80-point forensic analysis. No personal documents, passwords, photos, or
                browsing history are collected.
              </span>
            </div>
          </header>

          <div className={styles.reportGrid}>
            <section className={styles.scoreSection}>
              <div className={styles.scoreCard}>
                <div className={styles.scoreVisual}>
                  <svg className={styles.scoreRing} viewBox="0 0 100 100">
                    <circle className={styles.ringBg} cx="50" cy="50" r="45" />
                    <circle
                      className={styles.ringFill}
                      cx="50" cy="50" r="45"
                      style={{
                        strokeDashoffset: 283 - (283 * report.healthScore) / 100,
                        stroke: report.healthScore < 40 ? '#ff4444' : report.healthScore < 75 ? '#ff9800' : '#00e676',
                      }}
                    />
                  </svg>
                  <div className={styles.scoreText}>
                    <span className={styles.number}>{report.healthScore}</span>
                    <span className={styles.label}>Health Score</span>
                  </div>
                </div>
                <div className={styles.scoreStatus}>
                  <h3 style={{ textTransform: 'capitalize' }}>{report.scoreStatus} System Health</h3>
                  <div className={styles.countsGrid}>
                    <div className={`${styles.countBadge} ${styles.pass}`}><strong>{report.passCount}</strong><span>Passed</span></div>
                    <div className={`${styles.countBadge} ${styles.warning}`}><strong>{report.warningCount}</strong><span>Warning</span></div>
                    <div className={`${styles.countBadge} ${styles.critical}`}><strong>{report.criticalCount}</strong><span>Critical</span></div>
                    <div className={`${styles.countBadge} ${styles.not_detected}`}><strong>{report.notDetectedCount}</strong><span>Not Found</span></div>
                  </div>
                  <p>80 standard diagnostic points analysed.</p>
                </div>
              </div>

              <div className={styles.bottleneckCard}>
                <h3><Zap size={18} /> Primary Bottleneck</h3>
                <p><strong>{report.primaryBottleneck}</strong></p>
                <p style={{ marginTop: 8, opacity: 0.8, fontSize: '0.85rem' }}>{report.bottleneckExplanation}</p>
              </div>

              <div className={styles.overviewSection} style={{ gridTemplateColumns: '1fr' }}>
                <div className={styles.overCard}>
                  <div className={styles.overHeader}>
                    <Smartphone size={18} />
                    <h3>System Details</h3>
                  </div>
                  <div className={styles.overGrid}>
                    <div className={styles.overItem}><span>OS</span><strong>{valOrNA(overview.os)}</strong></div>
                    <div className={styles.overItem}><span>Manufacturer</span><strong>{valOrNA(overview.manufacturer)}</strong></div>
                    <div className={styles.overItem}><span>Model</span><strong>{valOrNA(overview.model)}</strong></div>
                    <div className={styles.overItem}><span>RAM</span><strong>{overview.ramGB > 0 ? `${overview.ramGB} GB` : valOrNA(0)}</strong></div>
                    <div className={styles.overItem}><span>C: Drive</span><strong>{overview.diskTotalGB > 0 ? `${overview.diskFreeGB} / ${overview.diskTotalGB} GB free` : valOrNA(0)}</strong></div>
                    <div className={styles.overItem}><span>Uptime</span><strong>{overview.uptimeDays} days</strong></div>
                    <div className={styles.overItem}><span>Startup Items</span><strong>{valOrNA(overview.startupCount)}</strong></div>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.contentSection}>
              {report.fixPlan && report.fixPlan.length > 0 && (
                <div className={styles.fixPlanCard}>
                  <h2><Wrench size={24} /> Prioritized Fix Plan</h2>
                  <div className={styles.fixList}>
                    {report.fixPlan.map((step: FixStep) => (
                      <div key={step.order} className={styles.fixStep}>
                        <div className={styles.stepNum}>{step.order}</div>
                        <div className={styles.stepContent}>
                          <h4>{step.title}</h4>
                          <p>{step.description}</p>
                          <div style={{ marginTop: 12, display: 'flex', gap: 12, fontSize: '0.75rem', opacity: 0.7 }}>
                            <span>Est. Time: {step.timeMinutes}m</span>
                            <span>Difficulty: {step.difficulty}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.sectionHeader}>
                <Search size={20} className={styles.searchIcon} />
                <h2>Detailed Findings ({(report.criticalFindings?.length || 0) + (report.warningFindings?.length || 0)})</h2>
              </div>

              {report.vendorUpdateGuidance && (
                <div className={styles.vendorUpdateCard}>
                  <h3><Wrench size={18} /> Recommended: Run {overview.manufacturer} update tool</h3>
                  <p className={styles.guidanceMeaning}>{report.vendorUpdateGuidance.meaning}</p>
                  <ol className={styles.guidanceSteps}>
                    {report.vendorUpdateGuidance.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              )}

              {(() => {
                const px = report.proExtras;
                const stale = !px;
                const sectionMissing = (label: string) => (
                  <div className={styles.notCollected}>
                    <Info size={14} /> {label} not collected by this scanner version. Download the latest PCCheckr scanner and run a fresh scan.
                  </div>
                );
                return (
                    <div className={styles.proInsights}>
                      <div className={styles.sectionHeader}>
                        <Activity size={20} className={styles.searchIcon} />
                        <h2>Pro Insights</h2>
                      </div>

                      {/* New Battery Intelligence Section (or fallback) */}
                      {report.battery?.present ? (
                        <BatteryIntelligenceCard battery={report.battery} />
                      ) : (
                        <div className={styles.desktopFallback}>
                          <Monitor size={32} style={{ marginBottom: '16px', opacity: 0.5 }} />
                          <h3>Desktop System Detected</h3>
                          <p>Battery diagnostics and wear analysis are not applicable to desktop PCs.</p>
                        </div>
                      )}

                    {/* Top RAM consumers */}
                    <div className={styles.insightCard}>
                      <h3>Top Memory Consumers</h3>
                      {stale || !px?.topProcesses?.top_ram?.length
                        ? sectionMissing('Top RAM process list')
                        : (
                          <table className={styles.insightTable}>
                            <thead><tr><th>Process</th><th>Instances</th><th>RAM (MB)</th></tr></thead>
                            <tbody>
                              {px.topProcesses.top_ram.slice(0, 10).map((p: any, i: number) => (
                                <tr key={i}><td>{p.name}</td><td>{p.instances}</td><td>{p.ram_mb}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      }
                    </div>

                    {/* Top CPU consumers */}
                    <div className={styles.insightCard}>
                      <h3>Top CPU Consumers</h3>
                      {stale || !px?.topProcesses?.top_cpu?.length
                        ? sectionMissing('Top CPU process list')
                        : (
                          <table className={styles.insightTable}>
                            <thead><tr><th>Process</th><th>Instances</th><th>CPU (sec)</th></tr></thead>
                            <tbody>
                              {px.topProcesses.top_cpu.slice(0, 10).map((p: any, i: number) => (
                                <tr key={i}><td>{p.name}</td><td>{p.instances}</td><td>{p.cpu_sec}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      }
                    </div>

                    {/* Browser memory */}
                    <div className={styles.insightCard}>
                      <h3>Browser Memory Usage</h3>
                      {stale || !px?.browserMemory?.browsers?.length
                        ? sectionMissing('Browser memory breakdown')
                        : (
                          <table className={styles.insightTable}>
                            <thead><tr><th>Browser</th><th>Processes</th><th>Total RAM (MB)</th></tr></thead>
                            <tbody>
                              {px.browserMemory.browsers.map((b: any, i: number) => (
                                <tr key={i}><td>{b.browser}</td><td>{b.processes}</td><td>{b.total_ram_mb}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      }
                      {!stale && px?.browserMemory?.browsers?.length > 0 && (
                        <p className={styles.insightTip}>
                          Tip: each browser tab is its own process. Close tabs you aren't actively using, or enable
                          "Memory Saver" in Chrome/Edge Settings &gt; Performance.
                        </p>
                      )}
                    </div>

                    {/* Startup apps */}
                    <div className={styles.insightCard}>
                      <h3>Startup Programs ({px?.startupApps?.count ?? overview.startupCount})</h3>
                      {stale || !px?.startupApps?.items?.length
                        ? sectionMissing('Detailed startup app list')
                        : (
                          <>
                            <table className={styles.insightTable}>
                              <thead>
                                <tr>
                                  <th>App Name</th>
                                  <th>Startup Type</th>
                                  <th>Recommendation</th>
                                </tr>
                              </thead>
                              <tbody>
                                {px.startupApps.items.slice(0, 20).map((s: any, i: number) => {
                                  const rec = startupRecommendation(s.name);
                                  const recColor =
                                    rec.keep === true ? '#00e676' :
                                    rec.keep === false ? '#ff9800' :
                                    '#9999a8';
                                  return (
                                    <StartupRow
                                      key={i}
                                      name={s.name}
                                      startupType={friendlyLocation(s.location)}
                                      recText={rec.text}
                                      recColor={recColor}
                                      rawLocation={s.location}
                                      rawCommand={s.command}
                                    />
                                  );
                                })}
                              </tbody>
                            </table>
                            <p className={styles.insightTip}>
                              To disable: right-click the Taskbar &gt; Task Manager &gt; Startup apps tab &gt; select the item &gt; Disable.
                              Do not disable anything related to Windows, security, or audio/graphics drivers.
                            </p>
                          </>
                        )
                      }
                    </div>

                    {/* Disk cleanup */}
                    <div className={styles.insightCard}>
                      <h3>Disk Cleanup Candidates</h3>
                      {stale || !px?.diskCleanup
                        ? sectionMissing('Disk cleanup sizes')
                        : (
                          <table className={styles.insightTable}>
                            <thead><tr><th>Location</th><th>Size (GB)</th></tr></thead>
                            <tbody>
                              <tr><td>User temp folder</td><td>{px.diskCleanup.temp_user_gb}</td></tr>
                              <tr><td>Windows temp folder</td><td>{px.diskCleanup.temp_windows_gb}</td></tr>
                              <tr><td>Recycle bin</td><td>{px.diskCleanup.recycle_bin_gb}</td></tr>
                              <tr><td>Downloads folder (total)</td><td>{px.diskCleanup.downloads_gb}</td></tr>
                            </tbody>
                          </table>
                        )
                      }
                    </div>

                    {/* Defender */}
                    <div className={styles.insightCard}>
                      <h3>Windows Defender Status</h3>
                      {stale || !px?.defender
                        ? sectionMissing('Defender status')
                        : (
                          <div className={styles.overGrid}>
                            <div className={styles.overItem}><span>Defender present</span><strong>{valOrNA(px.defender.defender_present)}</strong></div>
                            <div className={styles.overItem}>
                              <span>Real-time protection</span>
                              <strong className={px.defender.realtime_enabled ? styles.ok : styles.warn}>
                                {px.defender.realtime_enabled === null ? valOrNA(null) : px.defender.realtime_enabled ? 'ON' : 'OFF'}
                              </strong>
                            </div>
                            <div className={styles.overItem}><span>Antivirus enabled</span><strong>{valOrNA(px.defender.antivirus_enabled)}</strong></div>
                            <div className={styles.overItem}><span>Tamper protected</span><strong>{valOrNA(px.defender.tamper_protected)}</strong></div>
                            <div className={styles.overItem}><span>Signature age (days)</span><strong>{valOrNA(px.defender.signature_age_days)}</strong></div>
                            <div className={styles.overItem}><span>Registered AV products</span><strong>{valOrNA(px.defender.antivirus_count)}</strong></div>
                          </div>
                        )
                      }
                    </div>

                    {/* Windows update */}
                    <div className={styles.insightCard}>
                      <h3>Windows Update</h3>
                      {stale || !px?.windowsUpdate
                        ? sectionMissing('Windows Update status')
                        : (
                          <div className={styles.overGrid}>
                            <div className={styles.overItem}><span>Last installed update</span><strong>{valOrNA(px.windowsUpdate.last_install_date)}</strong></div>
                            <div className={styles.overItem}>
                              <span>Pending reboot</span>
                              <strong className={px.windowsUpdate.pending_reboot ? styles.warn : styles.ok}>
                                {px.windowsUpdate.pending_reboot ? 'Yes' : 'No'}
                              </strong>
                            </div>
                          </div>
                        )
                      }
                    </div>

                    {/* OEM */}
                    <div className={styles.insightCard}>
                      <h3>OEM &amp; BIOS</h3>
                      {stale || !px?.oem
                        ? sectionMissing('OEM/BIOS details')
                        : (
                          <div className={styles.overGrid}>
                            <div className={styles.overItem}><span>Manufacturer</span><strong>{valOrNA(px.oem.manufacturer)}</strong></div>
                            <div className={styles.overItem}><span>Model</span><strong>{valOrNA(px.oem.model)}</strong></div>
                            {px.oem.serial
                              ? <div className={styles.overItem}><span>Serial / Service Tag</span><strong>{px.oem.serial}</strong></div>
                              : null}
                            <div className={styles.overItem}><span>BIOS version</span><strong>{valOrNA(px.oem.bios_version)}</strong></div>
                            <div className={styles.overItem}><span>BIOS date</span><strong>{valOrNA(px.oem.bios_date)}</strong></div>
                          </div>
                        )
                      }
                    </div>

                    {/* Installed apps summary */}
                    <div className={styles.insightCard}>
                      <h3>Installed Apps ({px?.installedApps?.count ?? 'unknown'})</h3>
                      {stale || !px?.installedApps?.items?.length
                        ? sectionMissing('Installed app list')
                        : (
                          <>
                            <table className={styles.insightTable}>
                              <thead><tr><th>App</th><th>Publisher</th><th>Size (MB)</th></tr></thead>
                              <tbody>
                                {[...px.installedApps.items]
                                  .sort((a: any, b: any) => (b.size_mb || 0) - (a.size_mb || 0))
                                  .slice(0, 20)
                                  .map((a: any, i: number) => (
                                    <tr key={i}>
                                      <td>{a.name}</td>
                                      <td>{a.publisher || '—'}</td>
                                      <td>{a.size_mb ?? '—'}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                            <p className={styles.insightTip}>
                              Showing the 20 largest. Uninstall what you no longer use from Settings &gt; Apps &gt; Installed apps.
                            </p>
                          </>
                        )
                      }
                    </div>
                  </div>
                );
              })()}

              <div className={styles.issueList}>
                {[...(report.criticalFindings || []), ...(report.warningFindings || [])].map((f) => {
                  const finding = f as EnrichedCheck;
                  const g = finding.guidance;
                  const fallbackRecommendation =
                    finding.recommendation && finding.recommendation.trim().length > 0
                      ? finding.recommendation
                      : 'See the steps below to resolve this issue.';
                  return (
                    <div key={finding.id} className={`${styles.issueCard} ${styles[finding.status === 'fail' ? 'high' : 'medium']}`}>
                      <div className={styles.issueTop}>
                        <div className={styles.titleWrap}><h3>{finding.name}</h3></div>
                        <span className={styles.severityTag}>{finding.status === 'fail' ? 'Critical' : 'Warning'}</span>
                      </div>
                      <div className={styles.whySection}>
                        {g?.meaning || finding.explanation}
                      </div>
                      <div className={styles.evidenceLine}><strong>Value:</strong> {finding.value}</div>

                      {g ? (
                        <div className={styles.guidanceBlock}>
                          <div className={styles.guidanceMetaRow}>
                            <span className={`${styles.guidancePill} ${styles[`diff_${g.difficulty}`]}`}>
                              Difficulty: {g.difficulty}
                            </span>
                            <span className={styles.guidancePill}>
                              ⏱ ~{g.timeMinutes} min
                            </span>
                            <span className={`${styles.guidancePill} ${g.technicianNeeded ? styles.diff_hard : styles.diff_easy}`}>
                              Technician needed: {g.technicianNeeded ? 'Yes' : 'No'}
                            </span>
                          </div>

                          <div className={styles.guidanceSection}>
                            <h5><MousePointer2 size={14} /> Fix steps</h5>
                            <ol className={styles.guidanceSteps}>
                              {g.steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                          </div>

                          <div className={styles.guidanceSection}>
                            <h5><Check size={14} /> Expected result</h5>
                            <p>{g.expectedResult}</p>
                          </div>

                          <div className={`${styles.guidanceSection} ${styles.avoidSection}`}>
                            <h5><AlertTriangle size={14} /> What NOT to do</h5>
                            <ul className={styles.guidanceAvoid}>
                              {g.avoid.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                          </div>

                          <div className={styles.remBox} style={{ marginTop: 16 }}>
                            <h5><Activity size={14} /> Impact</h5>
                            <p>Health deduction: {finding.scoreImpact} points</p>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.remediationArea}>
                          <div className={styles.remGrid}>
                            <div className={styles.remBox}>
                              <h5><MousePointer2 size={14} /> Recommendation</h5>
                              <p>{fallbackRecommendation}</p>
                            </div>
                            <div className={styles.remBox}>
                              <h5><Activity size={14} /> Impact</h5>
                              <p>Health deduction: {finding.scoreImpact} points</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={styles.checksSummary}>
                <OverallHealthSummary score={report.healthScore} />
                <div className={styles.sectionHeader} style={{ marginTop: 32 }}>
                  <LayoutDashboard size={20} />
                  <h2>Checks Summary (80 Points)</h2>
                </div>

                {(report.categoryScores || []).map((cat: CategoryScore) => (
                  <div key={cat.category} className={styles.categoryGroup}>
                    <div className={styles.categoryHeader}>
                      <h3 style={{ textTransform: 'uppercase' }}>{cat.category}</h3>
                      <div className={styles.catCounts}>
                        <span className={`${styles.catCount} ${styles.pass}`}><Check size={12} /> {cat.passCount}</span>
                        <span className={`${styles.catCount} ${styles.warn}`}><AlertTriangle size={12} /> {cat.warningCount}</span>
                        <span className={`${styles.catCount} ${styles.fail}`}><AlertCircle size={12} /> {cat.failCount}</span>
                      </div>
                    </div>
                    <div className={styles.checkGrid}>
                      {cat.checks.map((check: CheckResult) => {
                        const isExpanded = expandedCheckId === check.id;
                        const canExpand = check.status === 'warning' || check.status === 'fail';
                        
                        return (
                          <div 
                            key={check.id} 
                            className={`${styles.checkItem} ${styles[check.status]} ${isExpanded ? styles.expanded : ''}`}
                            onClick={() => canExpand && setExpandedCheckId(isExpanded ? null : check.id)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <span className={styles.checkName}>{check.name}</span>
                              <div className={`${styles.checkStatusIcon} ${styles[check.status]}`}>
                                {check.status === 'pass' && <Check size={14} />}
                                {check.status === 'warning' && <AlertTriangle size={14} />}
                                {check.status === 'fail' && <AlertCircle size={14} />}
                                {check.status === 'not_detected' && <Info size={14} />}
                              </div>
                            </div>

                            {isExpanded && (
                              <div className={styles.checkExplanation}>
                                <p className={styles.expText}>
                                  {check.explanation || "No detailed explanation available for this check."}
                                </p>
                                {check.recommendation && (
                                  <div className={styles.expRec}>
                                    <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                                    <span><strong>Recommendation:</strong> {check.recommendation}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
              </div>
            </div>
          </ErrorBoundary>
        </div>

    <div className="wrap">
          {devCreditNotice && (
            <div className={styles.pdfErrorMsg} style={{ background: 'rgba(0,200,230,0.08)', borderColor: 'rgba(0,200,230,0.25)', color: '#8ec8d8' }}>
              <Info size={16} /><span>{devCreditNotice}</span>
            </div>
          )}
          {error && (
            <div className={styles.pdfErrorMsg}>
              <AlertTriangle size={16} /><span>{error}</span>
            </div>
          )}

          <footer className={styles.reportActions}>
            <button className={styles.primaryAction} onClick={downloadPDF} disabled={isGenerating}>
              {isGenerating
                ? <><Loader2 size={18} className={styles.spin} /> Generating PDF...</>
                : <><FileDown size={18} /> Download Pro PDF Report</>}
            </button>
            <button className={styles.secondaryAction} onClick={() => navigate('/dashboard')}>
              <LayoutDashboard size={18} /> Back to Dashboard
            </button>
          </footer>
        </div>
      </main>
      <Footer />
    </div>
  );
}
